import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  Skeleton,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getMyGotySelections, deleteGotySelection } from '../services/goty_api';
import { UserGotySelection, GotyCategory, GotyCategoryLabels } from '../types/Goty';
import { SITE_NAME } from '../constants/site';
import GotyGameSelectDialog from '../components/dialogs/GotyGameSelectDialog';

const EditGotyPage: React.FC = () => {
  const { year } = useParams<{ year: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [selections, setSelections] = useState<UserGotySelection[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<GotyCategory | null>(null);

  const currentYear = parseInt(year || '2025');
  const isMyGoty = location.pathname.startsWith('/mygoty/');

  useEffect(() => {
    fetchSelections();
  }, [currentYear]);

  const fetchSelections = async () => {
    try {
      setLoading(true);
      const response = await getMyGotySelections(currentYear, isMyGoty);
      setSelections(response.selections);
    } catch (error) {
      console.error('Failed to fetch GOTY selections:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategorySelection = (category: GotyCategory) => {
    return selections.find(s => s.category === category);
  };

  const handleEdit = (category: GotyCategory) => {
    setEditingCategory(category);
    setDialogOpen(true);
  };

  const handleDelete = async (category: GotyCategory) => {
    if (!window.confirm('この選択を削除しますか？')) return;

    try {
      await deleteGotySelection(currentYear, category, isMyGoty);
      await fetchSelections();
    } catch (error) {
      console.error('Failed to delete selection:', error);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleSaveSelection = async () => {
    await fetchSelections();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 4, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  const pageTitle = `GOTY ${currentYear} を編集 | ${SITE_NAME}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* ヘッダー */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            戻る
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {isMyGoty ? 'MyGOTY' : 'Game of the Year'} {currentYear}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isMyGoty
              ? 'その年に遊んだお気に入りのゲームを選択してください（発売年制約なし）'
              : '各カテゴリーでお気に入りのゲームを選択してください'}
          </Typography>
        </Box>

        {/* カテゴリー一覧 */}
        <Box sx={{ display: 'grid', gap: 3 }}>
          {Object.values(GotyCategory).filter(v => typeof v === 'number').map((category) => {
            const categoryNum = category as GotyCategory;
            const selection = getCategorySelection(categoryNum);
            const label = GotyCategoryLabels[categoryNum];

            return (
              <Card
                key={categoryNum}
                sx={{
                  p: 3,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {label.ja}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleEdit(categoryNum)}
                    >
                      {selection ? '変更' : '選択'}
                    </Button>
                    {selection && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(categoryNum)}
                      >
                        削除
                      </Button>
                    )}
                  </Box>
                </Box>

                {selection?.game ? (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {selection.game.cover && (
                      <Box
                        component="img"
                        src={selection.game.cover}
                        alt={selection.game.name}
                        sx={{ width: 80, height: 107, objectFit: 'cover', borderRadius: 1 }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {selection.game.japanese_name || selection.game.name}
                      </Typography>
                      {selection.comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          "{selection.comment}"
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    未選択
                  </Typography>
                )}
              </Card>
            );
          })}
        </Box>
      </Container>

      {/* ゲーム選択ダイアログ */}
      {editingCategory !== null && (
        <GotyGameSelectDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSave={handleSaveSelection}
          year={currentYear}
          category={editingCategory}
          currentGame={getCategorySelection(editingCategory)?.game}
          currentComment={getCategorySelection(editingCategory)?.comment}
          isMyGoty={isMyGoty}
        />
      )}
    </>
  );
};

export default EditGotyPage;
